import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement";
import Paginator from "Components/Common/Paginator";
import { DeleteIcon } from "assests/GmsIcons";
import React, { Dispatch, SetStateAction } from "react";
import PropTypes from "prop-types";
import ToggleButton from "Components/Common/components/Inputs/ToggleButton";
import TableCell from "Components/Common/components/TableCell";
import { SortType, DataRow, HeaderType } from "../RuleComponents/dataTypes";
import { RELATIONSHIPS } from "../../ISRConstants";


type TableProps = {
  headers: HeaderType[],
  data: DataRow[],
  recordPerPage: number,
  setRecordPerPage: (val: number) => void,
  handlePageChange: Dispatch<SetStateAction<number>>,
  handleSortByColumn: (map: string) => void,
  sort: SortType,
  loading: boolean,
  handleEdit: (index: number) => void,
  NoDataComponent: React.ComponentType,
  handleDelete: (index: number) => void,
  page: number,
  createMethod: () => void,
}

const Table = ({
  headers,
  data,
  recordPerPage,
  setRecordPerPage,
  handlePageChange,
  handleSortByColumn,
  sort,
  loading,
  handleEdit,
  NoDataComponent,
  handleDelete,
  page,
  createMethod,
}: TableProps) => {
  if (loading) {
    return (
      <div className="">
        <TableSkeletonElement />
      </div>
    );
  }

  const spaceObj = {
    "name": "25%",
    "description": "50%",
    "rule type": "15%",
    "enable/disable": "15%",
    "delete": "5%"
  };

  type TSpaceKey = keyof typeof spaceObj;

  return (
    <>
      <div id="rules-container" className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2">
        <div id="rules-header" className="tw-h-10 tw-place-content-center">
          <label id="rules-title" className="tw-font-bold tw-text-gray-500 tw-ml-2">
            {RELATIONSHIPS}
          </label>
          <button
            id="create-rule-btn"
            className="tw-text-blue-700 tw-float-right tw-mr-2"
            onClick={createMethod}
          >
            Create
          </button>
        </div>
        <div
          id="rules-table-container"
          className="table-container"
          style={{ width: "100%", height: "auto", overflowX: "auto" }}
        >
          {data && data.length > 0 ? (
            <table id="rules-table" className="tw-bg-[#DFE1E6] tw-w-full tw-table-fixed">
              <thead id="rules-table-header" className="tw-sticky tw-top-0 tw-bg-[#DFE1E6]">
                <tr>
                  {headers.map(({ label, ...rest }, index) => (
                    <th
                      id={`header-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      key={`${index}_${label}`}
                      className={`table-header-style`}
                      style={{
                        width: spaceObj[label?.toLowerCase() as TSpaceKey],
                      }}
                      scope="col"
                    >
                      <div
                        id={`header-content-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        className="tw-flex tw-align-middle"
                        onClick={() => {
                          rest.options?.sort && handleSortByColumn(rest.map);
                        }}
                      >
                        {["edit", "delete"].includes(label?.toLowerCase()) ? (
                          ""
                        ) : (
                          <label className="tw-text-[#5c5f62]">{label}</label>
                        )}
                        &nbsp;
                        <span className="tw-ms-2">
                          {rest.options?.sort &&
                            (sort.column === rest.map ? (
                              <i
                                className={`fas fa-sort-${sort.direction === "asc" ? "up" : "down"}`}
                              />
                            ) : (
                              <i className={"fas fa-sort"} />
                            ))}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody id="rules-table-body" className="tw-bg-white tw-divide-y tw-divide-[#F4F5F7]">
                {data
                  ?.slice(
                    page * recordPerPage - recordPerPage,
                    page * recordPerPage
                  )
                  ?.map((row, index) => (
                    <tr id={`rule-row-${index}`} key={index} className="tw-border tw-h-[42px]">
                      {headers.map(({ label, options, map }) => {
                        if (label.toLowerCase() === "delete") {
                          return (
                            <TableCell
                              id={`delete-cell-${index}`}
                              rowIndex={index}
                              key={`${index}_${label}`}
                            >
                              <button
                                id={`delete-btn-${index}`}
                                className="tw-cursor-pointer tw-flex tw-align-middle tw-text-danger"
                                onClick={() => handleDelete(index)}
                              >
                                <DeleteIcon />
                              </button>
                            </TableCell>
                          );
                        } else if (label === "Enable/Disable") {
                          return (
                            <TableCell
                              id={`toggle-cell-${index}`}
                              key={`${index}_${label}`}
                              rowIndex={index}
                            >
                              <ToggleButton
                                id={`toggle-btn-${index}`}
                                value={!(row["is_disabled"] as boolean)}
                                name={`toggle-${index}`}
                                onChange={(e) => {
                                  options?.handleSwitch && options.handleSwitch(e, row);
                                }}
                              />
                            </TableCell>
                          );
                        } else {
                          return (
                            <TableCell
                              id={`cell-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${index}`}
                              key={`${index}_${label}`}
                              rowIndex={index}
                            >
                              <span 
                                id={`content-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${index}`}
                                className={`tw-cursor-pointer tw-whitespace-break-spaces tw-w-full ${options?.isLink ? "tw-text-primary" : "tw-text-black"}`}
                                onClick={() => { if (options?.isLink) handleEdit(index) }}
                              >
                                {row[map]}
                              </span>
                            </TableCell>
                          );
                        }
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <NoDataComponent  />
          )}
        </div>
      </div>
      <div id="pagination-section">
        {data && data.length > 0 && (
          <div id="pagination-container" className="tw-flex tw-justify-end tw-align-bottom">
            &nbsp;
            <select
              id="records-per-page"
              className="tw-h-min tw-w-[70px] tw-border tw-p-1 tw-rounded-sm tw-mt-2 tw-mb-2"
              aria-label="Default select example"
              defaultValue={recordPerPage}
              onChange={(e) => setRecordPerPage(parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={10000}>All</option>
            </select>
            &nbsp;
            <Paginator
              id="rules-paginator"
              presentPage={page}
              recordPerPage={recordPerPage}
              totalRecords={data?.length}
              data={data}
              onPageChange={handlePageChange}
              simple={true}
              pageRange={""}
            />
          </div>
        )}
      </div>
    </>
  );
};

Table.propTypes = {
  headers: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  recordPerPage: PropTypes.number.isRequired,
  setRecordPerPage: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleSortByColumn: PropTypes.func.isRequired,
  sort: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  handleEdit: PropTypes.func.isRequired,
  NoDataComponent: PropTypes.elementType.isRequired,
  handleDelete: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  createMethod: PropTypes.func.isRequired,
};

export default Table;
